import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';

/**
 * Manages loading reference data (customers, jobs, tasks, rate codes, favorites)
 * and the current timesheet + entries.
 */
export default function useTimesheetData(timesheetId) {
  const [customers, setCustomers] = useState([]);
  const [rateCodes, setRateCodes] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [entries, setEntries] = useState([]);
  const [timesheet, setTimesheet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingStep, setLoadingStep] = useState('');
  const [debugInfo, setDebugInfo] = useState(null);
  const [error, setError] = useState(null);
  const loadStart = useRef(null);

  // Load reference data once
  useEffect(() => {
    setLoadingStep('Loading reference data...');
    Promise.all([
      api.getCustomers(),
      api.getRateCodes(null, 'Time'),
      api.getFavorites(),
    ]).then(([c, r, f]) => {
      setCustomers(c);
      setRateCodes(r);
      setFavorites(f);
    });
  }, []);

  // Load timesheet + entries when timesheetId changes
  useEffect(() => {
    if (!timesheetId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setDebugInfo(null);
    loadStart.current = Date.now();
    setLoadingStep('Loading timesheet & checking NAV status...');

    // Load timesheet and entries separately so one failure doesn't block the other
    const loadTs = api.getTimesheet(timesheetId)
      .then((ts) => {
        const elapsed = Date.now() - loadStart.current;
        console.log(`[TIMING] getTimesheet completed in ${elapsed}ms`);
        // Extract and store debug info from the response
        if (ts._debug) {
          setDebugInfo(ts._debug);
        }
        setTimesheet(ts);
        setLoadingStep('Loading entries...');
      })
      .catch((err) => { setError(err.message || 'Failed to load timesheet'); });

    const loadEntries = api.getTimeEntries(timesheetId)
      .then((ents) => {
        console.log(`[TIMING] getTimeEntries completed in ${Date.now() - loadStart.current}ms`);
        setEntries(ents);
      })
      .catch((err) => {
        console.error('Failed to load entries:', err);
        setEntries([]);
      });

    Promise.all([loadTs, loadEntries]).finally(() => {
      console.log(`[TIMING] All loading completed in ${Date.now() - loadStart.current}ms`);
      setLoading(false);
      setLoadingStep('');
    });
  }, [timesheetId]);

  const refreshEntries = useCallback(async () => {
    if (!timesheetId) return;
    try {
      const ents = await api.getTimeEntries(timesheetId);
      setEntries(ents);
    } catch (err) {
      console.error('Failed to refresh entries:', err);
    }
  }, [timesheetId]);

  // Replace one saved entry. A job/task has one rate code for the whole
  // timesheet (the server applies a change to all its dates), so the other
  // dates of that job/task take the saved entry's rate code too — otherwise a
  // later save of one of them would send the old code back.
  const updateSingleEntry = useCallback((updatedEntry) => {
    setEntries(prev => prev.map((e) => {
      if (e.TimeEntryID === updatedEntry.TimeEntryID) return updatedEntry;
      if (e.JobID === updatedEntry.JobID && e.JobTaskID === updatedEntry.JobTaskID && e.RateCodeID !== updatedEntry.RateCodeID) {
        return { ...e, RateCodeID: updatedEntry.RateCodeID, RateCodeName: updatedEntry.RateCodeName };
      }
      return e;
    }));
  }, []);

  const refreshFavorites = useCallback(async () => {
    const f = await api.getFavorites();
    setFavorites(f);
  }, []);

  const refreshTimesheet = useCallback(async () => {
    if (!timesheetId) return;
    const ts = await api.getTimesheet(timesheetId);
    if (ts._debug) {
      setDebugInfo(ts._debug);
    }
    setTimesheet(ts);
  }, [timesheetId]);

  return {
    customers,
    rateCodes,
    favorites,
    entries,
    timesheet,
    loading,
    loadingStep,
    debugInfo,
    error,
    refreshEntries,
    updateSingleEntry,
    refreshFavorites,
    refreshTimesheet,
  };
}
